import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { Icon } from "@iconify/react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAuth } from "../contexts/JWTContext";
import styles from "./Menu.module.css";
import { menuSections, menuTabs } from "../components/Submenu/menuTabs";
import CheckinExperience from "../components/CheckinExperience";
import ContactModal from "../components/Modals/ContactModal";

const getTabLabel = (tabId) =>
  menuTabs.find((tab) => tab.id === tabId)?.label || "เมนู";

const API_BASE_URL = String(process.env.REACT_APP_API_BASE_URL || "").replace(
  /\/+$/,
  "",
);

const normalizeNullableText = (value) => {
  const text = String(value ?? "").trim();
  return text && text.toUpperCase() !== "NULL" ? text : "";
};

const Menu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isInitialized, logout } = useAuth();
  const currentUser = useSelector((state) => state.user.currentUser);
  const playerBalance = useSelector((state) => state.user.playerBalance);
  const singleUsers = useSelector((state) => state.user.singleUsers);

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const rawTabFromQuery = searchParams.get("tab");
  const initialTabFromQuery =
    rawTabFromQuery === "cashier" ? "deposit" : rawTabFromQuery;
  const [activeTab, setActiveTab] = useState(initialTabFromQuery || "");
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [refundData, setRefundData] = useState({
    availableAmount: 0,
    refundPercentage: 0,
  });
  const [refundAvailableAmount, setRefundAvailableAmount] = useState(0);
  const pageRef = useRef(null);

  // Toast notification state for CheckinExperience
  const [, setToast] = useState({ show: false, message: "", type: "warning" });

  const showToast = (message, type = "warning") => {
    setToast({ show: true, message, type });
  };

  useEffect(() => {
    setActiveTab(initialTabFromQuery || "");
  }, [initialTabFromQuery]);

  useEffect(() => {
    if (!isInitialized) return;
    if (!isAuthenticated) {
      navigate("/login", { replace: true });
    }
  }, [isAuthenticated, isInitialized, navigate]);

  useEffect(() => {
    let viewportMeta = document.querySelector('meta[name="viewport"]');
    const originalContent = viewportMeta?.getAttribute("content") || "";
    let createdMeta = false;

    if (!viewportMeta) {
      viewportMeta = document.createElement("meta");
      viewportMeta.setAttribute("name", "viewport");
      document.head.appendChild(viewportMeta);
      createdMeta = true;
    }

    viewportMeta.setAttribute(
      "content",
      "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover",
    );

    return () => {
      if (!viewportMeta) return;
      if (createdMeta) {
        viewportMeta.remove();
        return;
      }
      viewportMeta.setAttribute("content", originalContent);
    };
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;

    html.classList.add("menu-page-lock");
    body.classList.add("menu-page-lock");

    return () => {
      html.classList.remove("menu-page-lock");
      body.classList.remove("menu-page-lock");
    };
  }, []);

  useEffect(() => {
    const timeoutIds = new Set();
    const prevScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";

    const resetWindowScroll = () => {
      if (
        window.scrollY !== 0 ||
        document.documentElement.scrollTop !== 0 ||
        document.body.scrollTop !== 0
      ) {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }

      const pageEl = pageRef.current;
      if (pageEl && pageEl.scrollTop !== 0) {
        pageEl.scrollTop = 0;
      }
    };

    const burstResetScroll = () => {
      window.requestAnimationFrame(resetWindowScroll);
      [60, 180, 320, 520, 760].forEach((delay) => {
        const id = window.setTimeout(() => {
          window.requestAnimationFrame(resetWindowScroll);
        }, delay);
        timeoutIds.add(id);
      });
    };

    const onVisibilityChange = () => {
      if (!document.hidden) {
        burstResetScroll();
      }
    };

    const onPageShow = () => {
      burstResetScroll();
    };

    const onViewportChange = () => {
      burstResetScroll();
    };

    burstResetScroll();

    window.addEventListener("scroll", burstResetScroll, { passive: true });
    window.addEventListener("pageshow", onPageShow);
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.visualViewport?.addEventListener("resize", onViewportChange);
    window.visualViewport?.addEventListener("scroll", onViewportChange);

    return () => {
      window.removeEventListener("scroll", burstResetScroll);
      window.removeEventListener("pageshow", onPageShow);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.visualViewport?.removeEventListener("resize", onViewportChange);
      window.visualViewport?.removeEventListener("scroll", onViewportChange);
      timeoutIds.forEach((id) => window.clearTimeout(id));
      window.history.scrollRestoration = prevScrollRestoration;
    };
  }, []);

  const handleItemClick = (tabId) => {
    if (!isAuthenticated) {
      return;
    }
    if (tabId === "contact") {
      setIsContactModalOpen(true);
      return;
    }
    setActiveTab(tabId);
    navigate(`/menu?tab=${tabId}`);
  };

  const activeTabConfig = menuTabs.find((tab) => tab.id === activeTab);
  const ActiveComp = activeTabConfig?.component;
  const isModalOpen = Boolean(activeTabConfig);

  const closeModal = useCallback(() => {
    setActiveTab("");
    navigate("/menu");
  }, [navigate]);

  const handleLogout = useCallback(() => {
    try {
      logout();
    } catch (e) {
      console.error("Logout error:", e);
    }

    try {
      localStorage.removeItem("user");
      localStorage.removeItem("userId");
      localStorage.removeItem("token");
      localStorage.removeItem("authToken");
      localStorage.removeItem("persist:root");
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) {
      // ignore
    }

    window.location.href = "/";
  }, [logout]);

  useEffect(() => {
    if (!isModalOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        closeModal();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [closeModal, isModalOpen]);

  // Get current user from singleUsers for CheckinExperience and latest status sync
  const currentUserForCheckin = useMemo(() => {
    if (!singleUsers) return null;
    if (Array.isArray(singleUsers)) {
      return singleUsers[0] || null;
    }
    if (Array.isArray(singleUsers?.data)) {
      return singleUsers.data[0] || null;
    }
    return singleUsers;
  }, [singleUsers]);

  const profileUser = currentUserForCheckin || currentUser || {};

  const resolvedUserId = useMemo(() => {
    if (profileUser?.id) return profileUser.id;
    try {
      const stored = localStorage.getItem("user");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.id) return parsed.id;
      }
    } catch (_) {}
    return localStorage.getItem("userId") || null;
  }, [profileUser]);

  const username =
    profileUser?.username || profileUser?.name || profileUser?.userName || "";

  useEffect(() => {
    let ignore = false;

    const loadRefundSummary = async () => {
      if (!resolvedUserId || !API_BASE_URL) {
        if (!ignore) {
          setRefundAvailableAmount(0);
          setRefundData({
            availableAmount: 0,
            refundPercentage: 0,
          });
        }
        return;
      }

      try {
        const [settingRes, cashbackRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/setting/cashback`),
          axios.get(
            `${API_BASE_URL}/agentbalance/cashbackclientcheck/${resolvedUserId}`,
          ),
        ]);
        const cashbackSetting = Array.isArray(settingRes?.data?.data)
          ? settingRes.data.data[0] || {}
          : {};
        const rows = Array.isArray(cashbackRes?.data?.data)
          ? cashbackRes.data.data
          : [];
        const total = rows.reduce(
          (sum, row) => sum + Number(row?.cashBackAmount || 0),
          0,
        );
        if (!ignore) {
          setRefundAvailableAmount(total);
          setRefundData({
            availableAmount: total,
            refundPercentage: Number(
              cashbackSetting?.paidpercent || rows[0]?.cashBackPercent || 0,
            ),
          });
        }
      } catch (_) {
        if (!ignore) {
          setRefundAvailableAmount(0);
          setRefundData({
            availableAmount: 0,
            refundPercentage: 0,
          });
        }
      }
    };

    loadRefundSummary();

    return () => {
      ignore = true;
    };
  }, [resolvedUserId]);

  // Check if turn/promo section is displayed (support multiple backend key styles)
  const withdrawConditionDetail = normalizeNullableText(
    profileUser?.withdrawConditionDetail ??
      profileUser?.WithdrawConditionDetail ??
      profileUser?.withdraw_condition_detail ??
      "",
  );
  const promotionDetail = normalizeNullableText(
    profileUser?.PromotionDetail ??
      profileUser?.promotionDetail ??
      profileUser?.promotion_detail ??
      "",
  );
  const promotionId = normalizeNullableText(
    profileUser?.PromotionID ??
      profileUser?.promotionId ??
      profileUser?.promotion_id ??
      "",
  );
  const promotionStatus = String(
    profileUser?.PromotionStatus ??
      profileUser?.promotionStatus ??
      profileUser?.promotion_status ??
      "",
  ).trim();
  const hasPromoSelection = Boolean(promotionDetail || promotionId);
  const hasTurnPromo = Boolean(withdrawConditionDetail || hasPromoSelection);
  const isPromoActive = promotionStatus.toUpperCase() === "ACTIVE";

  if (!isInitialized || !isAuthenticated) return null;

  return (
    <div className={styles.page} ref={pageRef}>
      <CheckinExperience
        currentUser={currentUserForCheckin}
        showToast={showToast}
      />

      <div
        className={styles.container}
        style={!hasTurnPromo ? { marginTop: 8 } : undefined}
      >
        <section className={styles.profileCard}>
          <div className={styles.profileHeader}>
            <div className={styles.avatar}>
              <Icon icon="fluent-color:person-16" width={42} />
            </div>
            <div className={styles.profileMeta}>
              <span className={styles.profileWelcome}>ยินดีต้อนรับ!</span>
              <span className={styles.userId}>{username || "ผู้ใช้งาน"}</span>
            </div>
          </div>
          <div className={styles.profileDivider} />
          <div className={styles.creditRow}>
            <div className={styles.creditInfo}>
              <Icon icon="twemoji:coin" width={18} />
              <span className={styles.creditLabel}>เครดิต</span>
            </div>
            <span className={styles.creditAmount}>
              {Number(playerBalance || 0).toLocaleString()}
            </span>
          </div>
        </section>

        {menuSections.map((section) => {
          const items = menuTabs.filter((tab) => tab.group === section.id);
          if (items.length === 0) return null;
          return (
            <section
              key={section.id}
              className={`${styles.sectionCard} ${
                section.id === "deposit" ? styles.depositSectionCard : ""
              } ${section.id === "cashback" ? styles.cashbackSectionCard : ""}`}
            >
              <div className={styles.sectionHeader}>
                <Icon icon={section.icon} className={styles.sectionIcon} />
                <span>{section.label}</span>
              </div>
              <div
                className={`${styles.menuGrid} ${
                  section.id === "cashback" ? styles.cashbackGrid : ""
                } ${
                  section.id === "deposit" ? styles.depositGridCompact : ""
                } ${section.id === "menu" ? styles.mainMenuGridCompact : ""}`}
              >
                {items.map((tab) =>
                  tab.id === "refund" ? (
                    <button
                      key={tab.id}
                      type="button"
                      className={styles.refundSummaryCard}
                      onClick={() => handleItemClick(tab.id)}
                    >
                      <div className={styles.refundSummaryIconArea}>
                        <div className={styles.refundSummaryIconWrap}>
                          <Icon
                            icon="flat-color-icons:money-transfer"
                            className={styles.refundSummaryIcon}
                          />
                        </div>
                      </div>
                      <div className={styles.refundSummaryDivider} />
                      <div className={styles.refundSummaryContent}>
                        <div className={styles.refundSummaryTitle}>
                          คืนยอดเสีย {refundData.refundPercentage}%
                        </div>
                        <div className={styles.refundSummaryAmount}>
                          {Number(refundAvailableAmount || 0).toLocaleString(
                            "th-TH",
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            },
                          )}
                        </div>
                        <div className={styles.refundSummaryAction}>
                          รับยอดเสีย
                        </div>
                      </div>
                    </button>
                  ) : (
                    <button
                      key={tab.id}
                      type="button"
                      className={`${styles.menuItem} ${
                        tab.id === activeTab ? styles.menuItemActive : ""
                      }`}
                      onClick={() => handleItemClick(tab.id)}
                    >
                      <div className={styles.menuIconWrap}>
                        <Icon icon={tab.icon} className={styles.menuIcon} />
                      </div>
                      <div className={styles.menuLabel}>{tab.label}</div>
                    </button>
                  ),
                )}

                {section.id === "menu" && (
                  <button
                    type="button"
                    className={`${styles.menuItem} ${styles.logoutMenuItem}`}
                    onClick={handleLogout}
                  >
                    <div className={styles.menuIconWrap}>
                      <Icon
                        icon="solar:logout-2-bold-duotone"
                        className={styles.menuIcon}
                      />
                    </div>
                    <div className={styles.menuLabel}>ออกจากระบบ</div>
                  </button>
                )}

                {/*} {section.id === "cashback" && hasTurnPromo && (
                  <div className={styles.mobileCashbackPromo}>
                    {withdrawConditionDetail && (
                      <div className={styles.turnPromoInlineCard}>
                        <Icon
                          icon="fluent-color:error-circle-48"
                          width={20}
                          height={20}
                        />
                        <span>{`คุณติดเทิร์น - ${withdrawConditionDetail}`}</span>
                      </div>
                    )}

                    {hasPromoSelection && (
                      <div
                        className={`${styles.turnPromoInlineCard} ${
                          isPromoActive
                            ? styles.promoInlineActive
                            : styles.promoInlineInactive
                        }`}
                      >
                        <Icon
                          icon="fluent-color:megaphone-loud-32"
                          width={20}
                          height={20}
                          style={{
                            filter: isPromoActive ? "none" : "grayscale(1)",
                          }}
                        />
                        <span>
                          {isPromoActive
                            ? promotionDetail
                              ? `คุณอยู่ระหว่างรับโปร - ${promotionDetail}`
                              : "คุณอยู่ระหว่างรับโปร"
                            : promotionDetail
                              ? `คุณได้เลือกโปร - ${promotionDetail}`
                              : "คุณได้เลือกโปร"}
                        </span>
                      </div>
                    )}
                  </div>
                )}*/}
              </div>
            </section>
          );
        })}
      </div>
      {isModalOpen && (
        <div className={styles.modalOverlay} onMouseDown={closeModal}>
          <div
            className={styles.modalCard}
            onMouseDown={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>{getTabLabel(activeTab)}</h3>
              <button
                type="button"
                className={styles.modalClose}
                onClick={closeModal}
                aria-label="ปิด"
              >
                ✕
              </button>
            </div>
            <div className={styles.modalBody}>
              {ActiveComp && <ActiveComp />}
            </div>
          </div>
        </div>
      )}
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
    </div>
  );
};

export default Menu;
